import { useTranslation } from 'react-i18next';
import type React from 'react';
import { Tooltip } from 'react-daisyui';
import { Chip } from '@leadsales/design-system/react';
import type { tComposerSendSealProps } from './ComposerSendSeal.types';
import './ComposerSendSeal.styles.scss';

/** Clases compartidas por los tres estados: mismo componente, misma caja. */
const SEAL_CLASS =
  'flex shrink-0 self-start mt-0.5 ml-2 whitespace-nowrap cursor-help';

/**
 * El envoltorio del hover. Sin texto NO envuelve: un `Tooltip` con mensaje
 * vacío deja un `data-tip=""` que daisyUI pinta como una burbuja en blanco.
 */
function SealTooltip(props: { text?: string; children: React.ReactNode }) {
  if (!props.text) return <>{props.children}</>;

  return (
    <Tooltip
      message={props.text}
      position="top"
      className={`ComposerSendSeal ${SEAL_CLASS}`}
    >
      {props.children}
    </Tooltip>
  );
}

/** La afordancia del hover, igual que `.pc-q` del prototipo. */
function HoverHint() {
  return (
    <span aria-hidden="true" className="ml-1 font-bold opacity-60">
      ⓘ
    </span>
  );
}

/**
 * El sello del compositor de texto libre (RET-2232, D-2/D-12/D-13).
 *
 * Sólo PINTA lo que `useComposerServiceSeal` ya decidió — no conoce la fecha
 * del corte ni ninguna otra regla de negocio.
 *
 * D-13: "Gratis" pesa más que "Con costo" — relleno verde sólido contra
 * contorno neutro (nunca índigo, que en esta pantalla ya significa "enlace").
 * D-12: el precio unitario va al HOVER (atributo nativo `title`), nunca en el
 * texto del sello — siempre es el mismo número y repetirlo entrena a mirarlo
 * en vez de la conversación.
 *
 * LAYOUT: `shrink-0` no es cosmético. El sello vive dentro de una fila flex
 * junto a un campo de texto `w-full`, y sin él la píldora se comprime hasta
 * casi cero ancho: el texto parte en dos líneas y el `border-radius` completo
 * del `Chip` convierte la caja en un CÍRCULO. Es `flex:none` en el prototipo,
 * por la misma razón.
 *
 * Se alinea ARRIBA y no al centro: el `<Textarea>` que tiene al lado mide
 * 64 px de alto y su texto empieza en la primera línea, así que un sello
 * centrado queda a media caja y el compositor entero se lee descuadrado.
 *
 * El `flex` del envoltorio no es decorativo: `.tooltip` de daisyUI es
 * `inline-block`, y un hijo en línea arrastra el interlineado de su caja de
 * texto —10 px medidos— que empujaban el sello por debajo de la primera
 * línea aunque estuviera `self-start`. Como flex, el sello es un ítem y no
 * una palabra, y el `mt-0.5` que queda es el ajuste fino contra el `p-1` del
 * textarea, no un número a ojo.
 *
 * EL HOVER NO ES EL `title` NATIVO, y la diferencia importa: el nativo tarda
 * cerca de un segundo, no se puede estilar, no existe para teclado y no
 * existe para táctil. Con el precio unitario viviendo ahí —y viviendo ahí
 * justamente para que no compita con el mensaje— un hover que no se descubre
 * equivale a no tener el número. Se usa el `Tooltip` de `react-daisyui`, que
 * es el que el resto del chat ya usa (`ChatMenu`, `CommonFields`): aparece al
 * instante, va con el estilo de la app y deja el `data-tip` en el DOM.
 *
 * El `ⓘ` es la afordancia del hover: sin él nadie sabe que hay algo que ver
 * pasando el mouse, y el precio unitario —el único número que este sello
 * tiene— se queda sin descubrir. Va como elemento aparte y no dentro de la
 * llave de i18n, para que el copy no arrastre un glifo.
 */
export default function ComposerSendSeal(props: tComposerSendSealProps) {
  const { seal } = props;
  const { t } = useTranslation('wallet');

  if (seal.kind === 'free-entry-point') {
    return (
      <SealTooltip text={props.tooltip}>
        <Chip
          data-testid="wallet-composer-send-seal"
          data-kind="free-entry-point"
          color="success"
          variant="filled"
          size="sm"
          className={SEAL_CLASS}
        >
          {/*
          D-41 · Bajo una hora se cuentan MINUTOS. Es la última hora de la
          ventana, cuando el número por fin sirve para decidir —contestar ya
          o dejarlo para mañana— y justo cuando redondear a horas lo borra.

          D-42 · Y bajo un MINUTO no se cuenta nada: se dice «Gratis» a secas.
          Que el sello llegue aquí ya significa que la ventana sigue abierta
          —esa pregunta la contesta el instante, no este número—, así que lo
          único en duda es qué cifra pintar, y «0 min» sería la única cifra
          que el sello puede mostrar sin que sea cierta. Callar el número y
          mantener la promesa dice exactamente lo que se sabe.
        */}
        {seal.hoursLeft > 0
          ? t('composer.seal.freeEntryPoint', { hours: seal.hoursLeft })
          : seal.minutesLeft > 0
            ? t('composer.seal.freeEntryPointMinutes', {
                minutes: seal.minutesLeft,
              })
            : t('composer.seal.free')}
          <HoverHint />
        </Chip>
      </SealTooltip>
    );
  }

  if (seal.kind === 'free') {
    return (
      <SealTooltip text={props.tooltip}>
        <Chip
          data-testid="wallet-composer-send-seal"
          data-kind="free"
          color="success"
          variant="filled"
          size="sm"
          className={SEAL_CLASS}
        >
          {t('composer.seal.free')}
          <HoverHint />
        </Chip>
      </SealTooltip>
    );
  }

  if (seal.kind === 'free-tier') {
    return (
      <SealTooltip text={props.tooltip}>
        <Chip
          data-testid="wallet-composer-send-seal"
          data-kind="free-tier"
          color="success"
          variant="filled"
          size="sm"
          className={SEAL_CLASS}
        >
          {/*
            RET-2283: misma etiqueta visible que "free" — las dos dicen
            "Gratis" al operador y la diferencia que importa (venir del cupo
            mensual, no del origen) vive en el `data-kind` y en el hover, no
            en una segunda palabra que DS-20 igual prohibiría cuantificar.
          */}
          {t('composer.seal.free')}
          <HoverHint />
        </Chip>
      </SealTooltip>
    );
  }

  if (seal.kind === 'free-tier-low') {
    return (
      <SealTooltip text={props.tooltip ?? seal.unitCostLabel ?? undefined}>
        {/*
          BUG-1140: mismo aspecto que «Con costo» (decisión de producto), sólo
          cambia la palabra. Sin cifra de restantes (DS-20).
        */}
        <Chip
          data-testid="wallet-composer-send-seal"
          data-kind="free-tier-low"
          color="default"
          variant="outlined"
          size="sm"
          className={SEAL_CLASS}
        >
          {t('composer.seal.freeTierLow')}
          <HoverHint />
        </Chip>
      </SealTooltip>
    );
  }

  return (
    <SealTooltip text={props.tooltip ?? seal.unitCostLabel ?? undefined}>
      <Chip
        data-testid="wallet-composer-send-seal"
        data-kind="paid"
        color="default"
        variant="outlined"
        size="sm"
        className={SEAL_CLASS}
      >
        {t('composer.seal.paid')}
        <HoverHint />
      </Chip>
    </SealTooltip>
  );
}
